import TrustedHtml from '@components/common/TrustedHtml/TrustedHtml'
import Close from '@components/icons/Close/Close'
import Button from '@components/ui/ButtonV2/Button'
import CheckboxV2 from '@components/ui/CheckboxV2/CheckboxV2'
import InputV2 from '@components/ui/InputV2/InputV2'
import { zodResolver } from '@hookform/resolvers/zod'
import { getRegion } from '@lib/utils'
import { selectCountry, selectLabels } from '@redux/globalSlice'
import clsx from 'clsx'
import React, { PropsWithChildren, useState } from 'react'
import { Dialog, Modal, ModalOverlay } from 'react-aria-components'
import { FormProvider, useForm } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { z } from 'zod'
import s from './BackInStock.module.scss'

const baseSchema = z.object({
  //customer
  email: z
    .string()
    .min(1, 'Please enter your email address.')
    .email({ message: 'Invalid email entered.' }),
  newsletter: z.boolean(),
})

export type NotifyMeFormValues = z.infer<typeof baseSchema>
interface Props {
  isOpen: boolean
  setOpen: (open: boolean) => void
  itemNumber: string
  data: string
}
type NativeAttrs = Omit<React.HTMLAttributes<HTMLDivElement>, keyof Props>

export type BackInStockModalProps = Props & NativeAttrs

const BackInStockModal = React.forwardRef<
  HTMLDivElement,
  PropsWithChildren<BackInStockModalProps>
>(
  (
    { className, children, setOpen, isOpen, itemNumber, data, ...props },
    ref,
  ) => {
    const [isLoading, setLoading] = useState<boolean>(false)
    const [isSuccess, setSuccess] = useState<boolean>(false)
    const methods = useForm<NotifyMeFormValues>({
      defaultValues: {
        email: '',
        newsletter: false,
      },
      resolver: zodResolver(baseSchema),
    })
    const country = useSelector(selectCountry)
    const labels = useSelector(selectLabels)

    const onSubmit = async (data: NotifyMeFormValues) => {
      setLoading(true)
      const request = await fetch(
        `https://order-integration.gubi.com/commerce/subscription/back-in-stock`,
        {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
          },
          body: JSON.stringify({
            email: data.email,
            itemNumber: itemNumber,
            region: getRegion(country.code),
            country: country.code?.toLowerCase(),
            newsletterConsent: data.newsletter,
          }),
        },
      )

      if (request.ok) {
        if (!labels.notifyMeSuccessMsg) {
          setOpen(false)
          methods.reset()
          setLoading(false)
          return
        } else {
          setSuccess(true)
          setLoading(false)

          return
        }
      }
    }

    const handleClose = () => {
      setLoading(false)
      setOpen(false)

      setTimeout(() => {
        methods.reset()
        setSuccess(false)
      }, 150)

      return
    }

    const successMsg = labels.notifyMeSuccessMsg.replaceAll(
      '$productName',
      data,
    )

    return (
      <FormProvider {...methods}>
        <ModalOverlay
          isDismissable
          isOpen={isOpen}
          onOpenChange={setOpen}
          className={s.overlay}
        >
          <Modal className={s.modal}>
            <Dialog>
              <form onSubmit={methods.handleSubmit(onSubmit)}>
                <div className={s.close}>
                  <button
                    className={s.closeButton}
                    type="button"
                    onClick={handleClose}
                  >
                    <Close className="h-6 w-6" />
                  </button>
                </div>
                <h2 className={clsx(s.heading, 'headerTwo')}>
                  {labels.notifyMeTitle}
                </h2>
                <div className={clsx(s.successMsg, { ['hidden']: !isSuccess })}>
                  <TrustedHtml className={s.description} html={successMsg} />
                  <Button
                    className={clsx(s.submit, s.successButton)}
                    type="button"
                    role="button"
                    onClick={handleClose}
                  >
                    Close
                  </Button>
                </div>
                <div
                  className={clsx(s.beforeSubmit, { ['hidden']: isSuccess })}
                >
                  <TrustedHtml
                    className={s.description}
                    html={labels.notifyMeDescription}
                  />
                  <InputV2 placeholder="Email" id={'email'} required />

                  <div className={s.checkboxWrapper}>
                    <CheckboxV2
                      className={s.checkbox}
                      {...methods.register('newsletter')}
                    >
                      <TrustedHtml
                        className={s.checkboxLabel}
                        html={labels.notifyMeNewsletterCheckboxLabel}
                      />
                    </CheckboxV2>
                  </div>

                  <Button
                    className={s.submit}
                    type="submit"
                    loading={isLoading}
                  >
                    {labels.notifyMeSubmitButtonLabel}
                  </Button>

                  <TrustedHtml
                    html={labels.notifyMeDisclaimer}
                    className={s.disclaimer}
                  />
                </div>
              </form>
            </Dialog>
          </Modal>
        </ModalOverlay>
      </FormProvider>
    )
  },
)

BackInStockModal.displayName = 'BackInStockModal'
export default BackInStockModal
